import styled from 'styled-components';
import React from 'react';
import PropTypes from 'prop-types';
import { isPortrait, height } from '../../utils/size';
import { LoadingSpinner } from '../LoadingSpinner';

function buttonHeight(numRows) {
    const def = isPortrait() ? 60 : numRows > 1 ? 65 : 88;
    const wrapper = document.querySelector('#ButtonsWrapper');
    if (!wrapper) {
        return def;
    }
    const available = height() - wrapper.offsetTop - 40;
    if (available < 40 || available > height() * 0.9) {
        return def;
    }
    const preferred =
        (available * 0.95) / numRows - (isPortrait() || numRows === 1 ? 16 : 8);
    return Math.max(Math.min(preferred, 100), 30);
}

const numRows = answersLength =>
    isPortrait() ? answersLength : answersLength > 3 ? 2 : 1;

const buttonFontSize = (numRows, longText, veryLongText) => {
    const validSizes = [16, 18, 20, 24, 26, 28];

    const space = buttonHeight(numRows);
    const preferred = 3 * Math.sqrt(space);
    let size = validSizes.find(s => s >= preferred) || validSizes[validSizes.length - 1];
    if (longText) size *= 18 / 28;
    if (veryLongText) size *= 16 / 20;
    return size;
};

const ButtonParent_OrientationBasedRules = props => {
    if (isPortrait()) {
        return `
            width: 100%;
            margin: 0 0px 16px 0px;
            padding: 0 8px;
        `;
    }
    return `
        margin: ${props.answersLength > 1 ? `16px 8px 0px 8px;` : `0`};
        width: ${
            props.answersLength === 3
                ? 'calc(33.33% - 16px)'
                : props.answersLength === 1
                ? '100%'
                : 'calc(50% - 16px)'
        };`;
};

export const ButtonParent = styled.div`
    float: left;
    position: relative;

    ${props => ButtonParent_OrientationBasedRules(props)};

    > div {
        display: none;
        position: absolute;
        left: 50%;
        margin-left: -11px;
        top: 50%;
        margin-top: -11px;
        width: 22px !important;
        height: 22px !important;

        ${props =>
            props.isRegisterOnlineSubmitting
                ? 'display: block;width: 32px !important;height: 32px !important;margin-top: -14px !important;margin-left: -14px !important;'
                : ''};
    }

    button {
        text-transform: 'none';
        font-size: ${props =>
            buttonFontSize(
                numRows(props.answersLength),
                props.longText,
                props.veryLongText
            ) + 'px'};
        line-height: ${props =>
            buttonFontSize(
                numRows(props.answersLength),
                props.longText,
                props.veryLongText
            ) +
            4 +
            'px'};

        ${props =>
            props.isRegisterOnlineSubmitting
                ? 'background: #ff245e !important; border: solid 1px #ff245e !important; text-indent: -99999px;'
                : ''};
    }
`;

const ButtonsWrapper_OrientationBasedRules = props => {
    if (isPortrait()) {
        return `
      left: ${props.mobileLeftPosition ? props.mobileLeftPosition + 'px' : '16px'};
      width: ${
          props.answersLength === 1 && !props.mobileStyled
              ? 'calc(100% - 32px)'
              : 'calc(100% - 16px)'
      };
      `;
    }
    return `
    width: ${props.answersLength === 1 ? 'calc(100% - 32px)' : 'calc(100% - 16px)'};
  `;
};

export const ButtonsWrapper = styled.div`
    flex: 1;

    > div {
        margin: ${props =>
            props.answersLength === 4
                ? '22px -8px 0 -8px'
                : props.answersLength === 1
                ? '54px 0 0 0'
                : '54px -8px 0 -8px'};
        position: absolute;
        opacity: 1;
        bottom: ${props => (isPortrait() && props.mobileStyled ? '55px' : '40px')};
        top: auto;

        ${props => ButtonsWrapper_OrientationBasedRules(props)};

        button {
            width: 100%;
            height: ${props => buttonHeight(numRows(props.answersLength)) + 'px'};
            cursor: pointer;
            background: #fff;
            border-radius: 4px;
            border: solid 1px #f1f1ee;
            font-weight: 600;
            margin: 0;
            vertical-align: top;
            padding-left: 10px;
            padding-right: 10px;
            outline: none;
            -webkit-tap-highlight-color: rgba(255, 255, 255, 1);

            &.selected {
                text-indent: -9999px;
                background: #ff245e;
                border: solid 1px #ff245e;
                outline: none;

                + div {
                    display: block;
                }
            }

            ${props =>
                isPortrait()
                    ? 'height: ' + buttonHeight(props.answersLength) + 'px;'
                    : ''};

            ${props =>
                props.isMobile
                    ? `
                        &:focus {
                            background: #ff245e;
                            border: solid 1px #ff245e;
                            color: #fff;
                            outline: none;
                        }
                    `
                    : `
                        &:hover {
                            background: #ff245e;
                            border: solid 1px #ff245e;
                            color: #fff;
                            outline: none;
                        }`};
        }
    }
`;

const Container = styled.div``;
export const ButtonsContainer = props => <Container>{props.children}</Container>;
ButtonsContainer.propTypes = {
    children: PropTypes.array.isRequired
};

export const AutoScalingButtonSet = props => {
    const children = Array.isArray(props.children) ? props.children : [props.children];
    const maxTextLength = children
        .map(child => child.props.text || '')
        .reduce((max, text) => Math.max(max, text.length), 0);
    return (
        <ButtonsWrapper
            id="ButtonsWrapper"
            data-button-wrapper="true"
            answersLength={children.length}
            isMobile={props.isMobile}
            mobileStyled={props.mobileStyled}
            mobileLeftPosition={props.mobileLeftPosition}
        >
            <ButtonsContainer>
                {children.map(child => (
                    <ButtonParent
                        key={child.key}
                        answersLength={children.length}
                        longText={maxTextLength > 20}
                        veryLongText={maxTextLength > 50}
                    >
                        {child}
                        {child.props.useLoadingSpinner && <LoadingSpinner />}
                    </ButtonParent>
                ))}
            </ButtonsContainer>
        </ButtonsWrapper>
    );
};
AutoScalingButtonSet.propTypes = {
    children: PropTypes.any.isRequired, // array of buttons
    isMobile: PropTypes.bool,
    mobileStyled: PropTypes.bool,
    mobileLeftPosition: PropTypes.number
};



// WEBPACK FOOTER //
// ./src/components/Buttons/AutoScalingButtonSet.jsx